<template>
  <Teleport defer to="#storeBrandingSlot" v-if="isSellerStore">
    <div :class="`${storeNamespace}-StoreBranding`">
      <div :class="`${storeNamespace}-StoreBreadcrumbs left`">
        <InertiaLink :class="`${storeNamespace}-StoreBreadcrumbs-link`" :href="storeBrandingUrl">Store Home</InertiaLink>
      </div>
      <div class="plus"></div>
      <InertiaLink :class="`${storeNamespace}-StoreLogo`" :href="storeBrandingUrl">{{ product.designer.name }}</InertiaLink>
    </div>
  </Teleport>

  <div>
    <!-- Fixed Header, Shows on Scroll Doubles as Mobile Page Header -->
    <header class="nylon-ProductPageHeader product fixie" :class="{ active: showPinnedHeader }" v-if="product.isStock || product.isLive">
      <div class="wrapper">
        <div class="title">
          <h1>{{ product.title }}</h1>
          <h3>
            <span class="by">By</span>
            <InertiaLink :href="product.designer.url">{{ product.designer.name }}</InertiaLink>
            <span v-if="product.type === 'clothing'" class="price">
              <em>—</em>
              <span v-if="userMadeFinalChoice">Size {{ selectedOptions[4].name }},</span>
              ${{ selectedVariant.price }}
            </span>
          </h3>
        </div>
        <aside>
          <div class="nylon-Buy">
            <div class="selected-options">
              <template v-if="product.type === 'clothing'">
                <span>
                  {{ selectedOptions[1].name }}
                  <em v-if="['Men', 'Women'].some((gender) => selectedOptions[1].name === gender)">'s</em>
                  {{ selectedOptions[2].name }}
                </span>
                <span>{{ selectedOptions[3].name }}</span>
                <span>Size {{ selectedOptions[4].name }}</span>
              </template>
              <template v-else>
                <span v-for="(choice, choiceIndex) in activeChoices">{{ choice.title }} {{ selectedOptions[choiceIndex].name }}</span>
              </template>
            </div>
            <div class="price">
              <span class="symbol">$</span>
              {{ selectedVariant.price }}
            </div>
            <button class="nylon-BuyButton" :disabled="isSubmitting || !selectedVariant.availableForSale || !userMadeFinalChoice" @click.prevent.stop="addToBag">
              <span v-show="!isSubmitting">
                <span class="desktop">Add to Cart</span>
                <span class="mobile">
                  <NylonIcon icon-name="cart-stroke" class="icon" />
                </span>
              </span>
              <VueSpinner v-show="isSubmitting" color="#ff523f" />
            </button>
          </div>
        </aside>
      </div>
    </header>

    <!-- Mobile Page Header for Non-Live Products -->
    <header class="nylon-ProductPageHeader product fixie non-live" v-if="!(product.isStock || product.isLive)">
      <div class="wrapper">
        <div class="title">
          <h1>{{ product.title }}</h1>
          <h3>
            <span class="by">By</span>
            <InertiaLink :href="product.designer.url">{{ product.designer.name }}</InertiaLink>
          </h3>
        </div>
      </div>
    </header>

    <!-- Page Header Proper, Shown at Desktop Sizes -->
    <header class="nylon-ProductPageHeader product">
      <div class="wrapper">
        <div class="title">
          <h1>{{ product.title }}</h1>
          <h3>
            <span>By</span>
            <InertiaLink :href="product.designer.url">{{ product.designer.name }}</InertiaLink>
          </h3>
        </div>
        <aside>
          <!-- Campaign Stats, Shown When 1 or Fewer Type Option Exist on Live Products -->
          <!-- Lifetime Stats, Shown When 1 or Fewer Type Option Exist on Expired Products -->
          <product-stats :product="product" :showGoal="!isSellerStore" v-if="showChoice(0) && activeChoices[0].options.length <= 1 && product.isPreOrder"></product-stats>
        </aside>
      </div>
    </header>

    <section
      class="nylon-ProductBlock"
      :class="{
        whiteout:
          (selectedVariant.iconColor === '#ffffff' || selectedVariant.iconColor === '#f0f0f3' || selectedVariant.iconColor === 'WhiteFleck') && !optsOutOfWhiteout && product.type != 'drinkware',
        'on-demand': product.isOnDemand,
      }"
    >
      <div class="watermark">
        {{ product.title }}
      </div>

      <!-- Store Campaign Product Header -->
      <header class="nylon-StoreCampaignHeader" v-if="isSellerStore">
        <div class="wrapper">
          <div class="title">
            <h1>{{ product.title }}</h1>
          </div>
          <aside>
            <!-- Campaign Stats, Shown When 1 or Fewer Type Option Exist on Live Products -->
            <!-- Lifetime Stats, Shown When 1 or Fewer Type Option Exist on Expired Products -->
            <product-stats :product="product" :showGoal="!isSellerStore" v-if="showChoice(0) && activeChoices[0].options.length <= 1 && product.isPreOrder"></product-stats>
          </aside>
        </div>
      </header>

      <div class="wrapper">
        <NylonProductGallery :images="carouselImages" :videos="carouselVideos" :models="carouselModels" :staticImages="carouselStaticImages" :tint="selectedVariant?.iconColor"></NylonProductGallery>

        <aside>
          <div
            class="nylon-ProductBlock-purchaseForm"
            :class="{
              expired: product.isExpired || (product.isStock && product.isSoldOut),
            }"
          >
            <!-- Type Toggle, Mobile Only -->
            <!-- Choices -->
            <template v-for="(choice, choiceIndex) in activeChoices">
              <NylonMultiChoice
                v-if="((activeChoices.length > 1 && choiceIndex === 0 && choice.options.length >= 2) || activeChoices.length <= 1 || choiceIndex > 0) && showChoice(choiceIndex)"
                :key="choice.name"
                :choice="choice"
                :choiceIndex="choiceIndex"
                :selected-option="choice.selectedOption"
                :userMadeFinalChoice="userMadeFinalChoice"
                @view-size-chart="viewSizeChart"
                @select-option="selectOption"
              ></NylonMultiChoice>
            </template>

            <!-- Disclaimers -->
            <product-disclaimers :product="product" :selected-variant="selectedVariant" :show-stock-live="showStockAndLiveSection" @view-size-chart="viewSizeChart"></product-disclaimers>

            <template v-if="showStockAndLiveSection">
              <div :ref="observeMainBuy" class="nylon-Buy" :class="{ 'l-sale': retailPrice !== discountedPrice }">
                <div v-if="retailPrice !== discountedPrice" class="price sale">
                  <div class="price-wrapper">
                    <div class="standard">
                      <span class="symbol">$</span>
                      {{ retailPrice }}
                    </div>
                    <div class="discounted">
                      <span class="symbol">$</span>
                      {{ discountedPrice }}
                    </div>
                  </div>
                </div>
                <div v-else class="price">
                  <div class="price-wrapper">
                    <span class="symbol">$</span>
                    {{ retailPrice }}
                  </div>
                </div>
                <button
                  class="nylon-BuyButton"
                  :disabled="isSubmitting || !selectedVariant.availableForSale || !userMadeFinalChoice"
                  @click.prevent.stop="addToBag"
                  :class="{ submitting: isSubmitting }"
                >
                  <span v-show="!isSubmitting">Add to Cart</span>
                  <VueSpinner v-show="isSubmitting" color="#ff523f" />
                </button>
              </div>
              <transition name="fade">
                <div v-if="message && message.type === 'error'" class="feedback-message u-push--reverse">
                  <message-block :type="message.type" :is-dismissable="false">
                    <p>{{ message.text }}</p>
                  </message-block>
                </div>
              </transition>

              <product-stats
                :product="product"
                :showGoal="!isSellerStore"
                v-if="product.isPreOrder"
                :class="{
                  'u-hideAboveMobile': activeChoices[0].options.length <= 1,
                }"
              ></product-stats>

              <div class="nylon-CampaignInfo" :class="{ expired: product.isPreOrder && product.isExpired }">
                <div class="nylon-ProductBlock-campaignNotice">
                  <template v-if="product.isPreOrder && product.preorderStatus === 'live'">
                    <p>
                      The last time to buy this is
                      <strong> {{ toLocaleString(product.dates.expiresAt, "DATETIME_FULL") }} </strong>. Orders usually ship within {{ product.dates.fullShipping.relativeRangeFormatted }} of the end
                      of the campaign (
                      <i>{{ product.dates.fullShipping.rangeFormatted }}</i>
                      ).
                      <a href="https://support.cottonbureau.com/article/32-when-will-i-get-my-order">Learn More...</a>
                    </p>
                    <product-reminder v-if="showReminder" :product="product" :viewer-email="viewerEmail"></product-reminder>
                  </template>
                  <p v-else-if="selectedVariant.subscriptions && selectedVariant.subscriptions.length">
                    If you place a subscription order today (or any time before
                    {{ toLocaleString(currentSubscription().cloneAfter, { month: "short", day: "numeric" }) }}), your first journal will ship within
                    {{ selectedVariant.fullShipping.relativeRangeFormatted }}
                    ({{ selectedVariant.fullShipping.rangeFormatted }}), and your second journal will ship in time for the beginning of the next quarter ({{
                      toLocaleString(currentSubscription().dueBy, { month: "short", day: "numeric" })
                    }}).
                  </p>
                  <p v-else>
                    <template v-if="selectedVariant.isBackorder || alwaysShowsBackorderCopy">
                      Orders expected to ship by
                      <i>{{ selectedVariant.fullShipping.rangeFormatted }}</i
                      >.
                      <a href="https://support.cottonbureau.com/article/32-when-will-i-get-my-order">Learn More...</a>
                    </template>
                    <template v-else>
                      Orders usually ship within
                      {{ selectedVariant.fullShipping.relativeRangeFormatted }}
                      (
                      <i>{{ selectedVariant.fullShipping.rangeFormatted }}</i>
                      ).
                      <a href="https://support.cottonbureau.com/article/32-when-will-i-get-my-order">Learn More...</a>
                    </template>
                  </p>
                </div>
              </div>
            </template>
          </div>

          <!-- Expired -->
          <template v-if="(product.isPreOrder && product.isExpired) || (product.isStock && product.isSoldOut)">
            <NylonProductRequest :product="product" :viewer-email="viewerEmail" />

            <!-- Show When There is Less Than 2 Type Options, Otherwise it Shows Up in the Header -->
            <product-stats
              :product="product"
              :showGoal="!isSellerStore"
              :class="{
                'u-hideAboveMobile': activeChoices[0].options.length <= 1,
              }"
            ></product-stats>
          </template>

          <template v-if="isDisabledLegacyClockwiseOnDemandProduct">
            <div class="product-disclaimer-wrapper">
              <div class="product-disclaimer active">
                <figure>
                  <span>!</span>
                </figure>
                <div class="figcaption">
                  <p>
                    This product is not currently available. Please email
                    <a href="mailto:support@cottonbureau.com">support@cottonbureau.com</a>
                    if you would like to see it come back.
                  </p>
                </div>
              </div>
            </div>
          </template>
          <template v-if="user?.id == product.designer.id && product.isOnDemandV2 && product.type === 'clothing'">
            <div class="product-disclaimer-wrapper product-disclaimer-wrapper--convert">
              <div class="product-disclaimer active">
                <figure>
                  <span>→</span>
                </figure>
                <div class="figcaption">
                  <p>
                    Did you know Cotton Bureau now has support for on demand phone cases, hats, and totes? You can use the artwork from this product to
                    <InertiaLink :href="createFromProductUrl()">get started</InertiaLink>.
                  </p>
                </div>
              </div>
            </div>
          </template>
        </aside>
      </div>
    </section>

    <section class="nylon-StoreProductContent" v-if="isSellerStore" :class="{ expired: !product.isLive }">
      <!-- Store Products Only -->
      <div class="wrapper">
        <div class="other-products">
          <header>
            <h4>Check These Out Too</h4>
          </header>

          <div class="wrapper">
            <NylonProductCard v-for="related in relatedProducts" :key="related.id" :product="related" variant="grid" />
          </div>
        </div>

        <NylonProductInfo :product="product" hide-empty-description />
      </div>
    </section>

    <section class="nylon-ProductContent" v-if="!isSellerStore">
      <div class="wrapper">
        <NylonProductInfo :product="product" />
        <div class="creator">
          <div class="wrapper">
            <header>
              <InertiaLink :href="product.designer.url">
                <figure
                  v-if="product.designer.avatarUrl"
                  class="avatar"
                  :style="{
                    'background-image': `url(${product.designer.avatarUrl})`,
                  }"
                ></figure>
                <figure v-else class="avatar avatar--default"><NylonIcon icon-name="uiUser" /></figure>
              </InertiaLink>
              <h3>
                <InertiaLink :href="product.designer.url">{{ product.designer.name }}</InertiaLink>
              </h3>
              <h5>{{ product.designer.location }}</h5>
            </header>
            <div class="bio">
              <p>{{ product.designer.bio }}</p>
            </div>
            <div class="links">
              <NylonSocialIcon
                v-for="(social, socialKey) in product.designer.formattedSocials"
                :key="socialKey"
                :class="[social.class]"
                :icon="social.icon"
                :name="social.name"
                :href="social.url"
                rel="me"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="nylon-ProductCrossSell" v-if="!isSellerStore">
      <div class="pattern"></div>

      <div class="related">
        <header>
          <h4>Check These Out Too</h4>
          <span class="line"><i></i></span>
        </header>

        <NylonProductCardStrip :products="relatedProducts" />
      </div>
    </section>

    <section class="nylon-ApparelResources" v-if="['clothing', 'phonecase', 'hat', 'beanie', 'tote', 'drinkware', 'sticker'].includes(product.type) && !isMysteryTee">
      <header>
        <div class="wrapper">
          <div class="preface">
            <h4>
              <span class="title">Find Your {{ findYour }}</span>
              <span class="line"><i></i></span>
            </h4>
            <article>
              <h3>{{ selectedBlank.title }}</h3>
              <div class="nylon-DropdownNav">
                <select class="nylon-DropdownNav-select" v-model="selectedBlankIndex">
                  <option v-for="(blank, blankIndex) in blankDetails" :value="blankIndex">
                    {{ blank.title }}
                  </option>
                </select>
              </div>
              <div class="text">
                <p></p>
              </div>
            </article>
          </div>
          <div class="navigation">
            <nav class="sub-nav">
              <ul>
                <li v-for="(blank, blankIndex) in blankDetails">
                  <a href="#" :class="{ active: selectedBlankIndex === blankIndex }" @click.prevent="selectedBlankIndex = blankIndex">
                    {{ blank.title }}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </header>
      <main>
        <div class="sizing" v-if="product.type === 'clothing'">
          <h6>Sizing</h6>
          <product-size-chart :sizing="selectedBlank.sizing" />
        </div>
        <div class="about-product" v-else>
          <h6>About This Product</h6>
          <div class="about-product-text">
            <div class="nylon-Text">
              <p v-if="product.type === 'phonecase'">
                High quality, no-nonsense cases that get the job done. Light-weight and form-fitting. Sturdy yet svelte. Beautifully designed with easy access to all ports. Printed in the United
                States of America. Not for the faint of heart.
              </p>
              <p v-else-if="product.type === 'hat'">
                Premium materials, excellent craftsmanship. These hats work hard and last long. Don’t leave home without one. Embroidered in Pittsburgh, Pennsylvania.
              </p>
              <p v-else-if="product.type === 'beanie'">Premium materials, excellent craftsmanship. These beanies keep you warm and look great. Embroidered in Pittsburgh, Pennsylvania.</p>
              <p v-else-if="product.type === 'tote'">
                Fabric bags made from 100% cotton canvas material. Useful and reusable for years to come. Fits 24 cans of your favorite beverage without breaking a sweat. Construction that won’t quit
                before you do.
              </p>
              <p v-else-if="product.type === 'drinkware'">
                Doctors say to drink at least 10 cups of water per day. We say, who needs water when you can drink other stuff? We’re not doctors though. Cotton Bureau drinkware is sure to keep your
                beverages hot (or cold), just the way you like them. And you’ll look good too.
              </p>
              <p v-else-if="product.type === 'sticker'">
                Stick ‘em here, stick ‘em there, stick ‘em everywhere. Finest quality, vinyl kiss-cut stickers for your laptop, tumbler, or best friend’s car.
              </p>
            </div>
          </div>
        </div>
        <product-details :product="product" :selected-blank="selectedBlank" />
      </main>
    </section>

    <Teleport v-if="isAdmin" defer to="#storefront-dock">
      <div class="pinned-menu">
        <span class="pinned-menu-dot" :class="product.designer.accountManagement ? 'pinned-menu-dot--green' : 'pinned-menu-dot--red'"></span>
        <form>
          <div class="pinned-menu-group pinned-menu-group--muted">
            <a :href="`/admin/edit-seller/${product.designer.id}`">
              <figure>
                <NylonIcon icon-name="uiUser" class="icon" />
              </figure>
              <span style="text-transform: capitalize">{{ product.designer.sellerType }}</span>
            </a>
          </div>
          <div class="pinned-menu-group">
            <a :href="editProductUrl">
              <figure>
                <NylonIcon icon-name="wrenches" class="icon" />
              </figure>
              <span>Edit #{{ product.id }}</span>
            </a>
          </div>
          <div class="pinned-menu-group">
            <a @click.prevent="cacheMockups" :class="{ 'is-disabled': isMockupCaching }">
              <figure>
                <NylonIcon icon-name="uiRefresh" class="icon" />
              </figure>
              <span>
                {{ isMockupCaching ? "Caching…" : "Cache Mockups" }}
              </span>
            </a>
          </div>
          <div class="pinned-menu-group">
            <a @click.prevent="updateSearchIndex" :class="{ 'is-disabled': isSearchIndexUpdating }">
              <figure>
                <NylonIcon icon-name="uiRefresh" class="icon" />
              </figure>
              <span>
                {{ isSearchIndexUpdating ? "Updating…" : "Update Search Index" }}
              </span>
            </a>
          </div>
          <div class="pinned-menu-group">
            <NylonCheckbox :model-value="isFeatured" label="Featured" :tight="false" :disabled="isFeaturedSaving" @update:model-value="onFeaturedToggle" />
            <select class="nylon-FormField-select" v-model="featuredTheme" :disabled="isFeaturedSaving" @change="saveFeatured">
              <option value="red">Red</option>
              <option value="blue">Blue</option>
              <option value="green">Green</option>
              <option value="orange">Yellow</option>
              <option value="grey">Grey</option>
              <option value="black">Black</option>
            </select>
          </div>
        </form>
      </div>
    </Teleport>

    <Teleport v-if="user?.id == product.designer.id" defer to="#storefront-dock">
      <div class="pinned-button">
        <figure>
          <NylonIcon icon-name="wrenches" class="icon" />
        </figure>
        <div class="links">
          <InertiaLink :href="sellerEditProductUrl">Edit Product</InertiaLink>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
// The gallery and the purchase form's parts are above the fold, so they load with the page: as async
// components each rendered nothing until its own chunk arrived, and the page jumped as they filled in
// (CLS ~1.2 on mobile), and the main image waited on a chain of chunk requests.
import NylonProductGallery from "@/nylon/components/NylonProductGallery.vue";
import NylonMultiChoice from "@/nylon/components/NylonMultiChoice.vue";
import ProductDisclaimers from "@/product/components/product-disclaimers.vue";
import ProductStats from "@/product/components/product-stats.vue";
const ProductSizeChart = defineAsyncComponent(() => import("@/product/components/product-size-chart.vue"));
const ProductDetails = defineAsyncComponent(() => import("@/product/components/product-details.vue"));
const NylonProductInfo = defineAsyncComponent(() => import("@/nylon/components/NylonProductInfo.vue"));
const ProductReminder = defineAsyncComponent(() => import("@/product/components/product-reminder.vue"));
import NylonProductRequest from "@/nylon/components/NylonProductRequest.vue";
const NylonProductCard = defineAsyncComponent(() => import("@/nylon/components/NylonProductCard.vue"));
const NylonProductCardStrip = defineAsyncComponent(() => import("@/nylon/components/NylonProductCardStrip.vue"));
const NylonCheckbox = defineAsyncComponent(() => import("@/nylon/components/NylonCheckbox.vue"));
const VueSpinner = defineAsyncComponent(() => import("vue3-spinners").then((m) => m.VueSpinner));
const MessageBlock = defineAsyncComponent(() => import("@/components/message-block.vue"));
import { DateTime } from "luxon";
import { defineAsyncComponent } from "vue";
import { Link as InertiaLink } from "@inertiajs/vue3";
import { mapActions, mapState, mapStores } from "pinia";
import StorefrontLayout from "@/layout/storefront-layout.vue";
import NylonSocialIcon from "@/nylon/components/NylonSocialIcon.vue";
import { useUserStore } from "@/store/user.js";
import { useCartStore } from "@/store/cart.js";
import { useAdminProductsStore } from "@/admin/store/products.js";
import { useProductSelectorStore } from "@/store/product-selector.js";
import { retrieveErrors } from "@/lib/api-errors.js";
import { clearMessage, showError } from "@/lib/message-bag.js";
import { trackEvent } from "@/lib/analytics.js";
import { trackCortexPixel } from "@/lib/facebook-pixel.js";
import { BACKORDER_COPY_PRODUCT_IDS, CORTEX_USER_ID, MYSTERY_TEE_PRODUCT_ID, NO_WHITEOUT_USER_IDS } from "@/lib/storefront-overrides.js";
import qs from "querystringify";
import Velocity from "velocity-animate";

const store3SkinModules = import.meta.glob("../../scss/nylon-stores/skins/store_skin_*.scss");
const storeSkinModules = import.meta.glob("../../scss/poly-stores/skins/store_skin_*.scss");
const newStoresStyles = () => import("../../scss/custom-stores/custom-stores.scss");
const store3BaseStyles = () => import("../../scss/nylon-stores/nylon-stores.scss");
const storeBaseStyles = () => import("../../scss/poly-stores/poly-stores.scss");
import "@styles/product/product.scss";

export default {
  name: "ProductPage",
  layout: StorefrontLayout,

  components: {
    InertiaLink,
    NylonSocialIcon,
    NylonProductGallery,
    VueSpinner,
    ProductReminder,
    NylonProductRequest,
    ProductStats,
    NylonMultiChoice,
    ProductSizeChart,
    ProductDetails,
    ProductDisclaimers,
    NylonProductInfo,
    NylonProductCard,
    NylonProductCardStrip,
    NylonCheckbox,
    MessageBlock,
  },

  inject: {
    toggleCheckout: { default: () => () => {} },
  },

  props: {
    product: { type: Object, required: true },
    relatedProducts: { type: Array, default: () => [] },
    // Built server-side by App\Services\ProductBlankDetails: {id, code, metadata, title, sizing?, caseType?}.
    blankDetails: { type: Array, default: () => [] },
    initialIsSellerStore: { type: Boolean, default: false },
    viewerEmail: { type: [String, null], default: null },
    useNewStoreStyles: { type: Boolean, default: false },
    inlineStoreCss: { type: String, default: "" },
  },

  data() {
    return {
      selectedBlankIndex: 0,
      isFeatured: this.product.isFeatured,
      featuredTheme: this.product.featuredTheme,
      isFeaturedSaving: false,
      isMockupCaching: false,
      isSearchIndexUpdating: false,
      isSubmitting: false,
      message: null,
      // From sm up, the fixed header slides in once the main Buy block has
      // scrolled up out of view (at xs the header is static and always shown).
      showPinnedHeader: false,
    };
  },

  computed: {
    ...mapStores(useCartStore),
    ...mapStores(useProductSelectorStore),
    ...mapState(useUserStore, ["isAdmin"]),

    // Per-product / per-seller one-offs (ids in lib/storefront-overrides.js).
    alwaysShowsBackorderCopy() {
      return BACKORDER_COPY_PRODUCT_IDS.includes(Number(this.product.id));
    },

    isMysteryTee() {
      return Number(this.product.id) === MYSTERY_TEE_PRODUCT_ID;
    },

    optsOutOfWhiteout() {
      return NO_WHITEOUT_USER_IDS.includes(Number(this.product.designer.id));
    },

    // Choice-engine state + derivations live in useProductSelectorStore; re-expose
    // the fields the template reads so `this.selectedVariant` etc. keep working.
    // The carousel/price getters (`carouselImages`…/`retailPrice`/`discountedPrice`)
    // moved from component computeds to store getters — single source of truth.
    ...mapState(useProductSelectorStore, [
      "selectedVariant",
      "activeChoices",
      "selectedOptions",
      "userMadeFinalChoice",
      "carouselImages",
      "carouselVideos",
      "carouselModels",
      "carouselStaticImages",
      "retailPrice",
      "discountedPrice",
    ]),

    isSellerStore() {
      return this.initialIsSellerStore || this.product?.designer?.isStore;
    },

    // Store-chrome class namespace, matching the skin-loading branch below:
    // v1 sellers (initialIsSellerStore) style under .poly-Store*; store3 / new
    // sellers under .nylon-Store*.
    storeNamespace() {
      return this.initialIsSellerStore ? "poly" : "nylon";
    },

    storeBrandingUrl() {
      return this.product?.designer?.url;
    },

    showReminder() {
      const nowPlus36Hours = DateTime.now().plus({ hours: 36 });
      const expiration = DateTime.fromISO(this.product.dates.expiresAt);

      return nowPlus36Hours < expiration;
    },

    // Blank Details
    selectedBlank() {
      return this.blankDetails[this.selectedBlankIndex];
    },

    findYour() {
      switch (this.product.type) {
        case "clothing":
          return "Fit";
        case "phonecase":
          return "Case";
        case "hat":
          return "Hat";
        case "beanie":
          return "Beanie";
        case "tote":
          return "Tote";
        case "drinkware":
          return "Drinkware";
        case "sticker":
          return "Sticker";
      }
    },

    editProductUrl() {
      return `/admin/edit-product/${this.product.id}`;
    },

    sellerEditProductUrl() {
      return `/account/products/${this.product.id}`;
    },

    isDisabledLegacyClockwiseOnDemandProduct() {
      return this.product.isDisabledLegacyClockwiseOnDemand;
    },

    // Gates the stock/live disclaimer stack + the buy/stats/nylon-CampaignInfo block.
    // Single source for both <product-disclaimers :show-stock-live> and the
    // sibling `<template v-if>`.
    showStockAndLiveSection() {
      return (this.product.isStock && !this.product.isSoldOut && !this.isDisabledLegacyClockwiseOnDemandProduct) || this.product.isLive;
    },

    ...mapState(useUserStore, ["user"]),
    ...mapStores(useAdminProductsStore),
  },

  watch: {
    selectedVariant: function (newVariant, oldVariant) {
      if (newVariant) {
        let findFunction = (blank) => blank.code === newVariant.code;

        if (this.product.type === "phonecase") {
          findFunction = (blank) => blank.caseType === newVariant.sizeReadable;
        }

        const index = this.blankDetails.findIndex(findFunction);

        if (index >= 0) {
          this.selectedBlankIndex = index;
        }
      }
    },
  },

  created() {
    // Seed the choice engine unconditionally (CLAUDE.md store-seed rule); init()
    // $resets first so a prior product never leaks across Inertia page swaps.
    this.productSelectorStore.init(this.product);
  },

  async mounted() {
    this.sendAnalyticData();

    this.onHashChange = () => {
      this.productSelectorStore.syncFromHash();
    };
    window.addEventListener("hashchange", this.onHashChange);

    if (this.isSellerStore) {
      try {
        if (this.initialIsSellerStore) {
          await storeBaseStyles();
          const skinPath = `../../scss/poly-stores/skins/store_skin_${this.product.designer.id}.scss`;
          if (storeSkinModules[skinPath]) {
            await storeSkinModules[skinPath]();
          }
        } else if (this.useNewStoreStyles) {
          await newStoresStyles();
        } else {
          await store3BaseStyles();
          const skinPath = `../../scss/nylon-stores/skins/store_skin_${this.product.designer.id}.scss`;
          if (store3SkinModules[skinPath]) {
            await store3SkinModules[skinPath]();
          }
        }
      } catch (err) {
        console.warn("[product] failed to load store styles", err);
      }
    }

    if (this.inlineStoreCss) {
      this.injectedStyleEl = document.createElement("style");
      this.injectedStyleEl.dataset.productStore = this.product.designer.id;
      this.injectedStyleEl.textContent = this.inlineStoreCss;
      document.head.appendChild(this.injectedStyleEl);
    }
  },

  beforeUnmount() {
    this.mainBuyObserver?.disconnect();
    if (this.onHashChange) {
      window.removeEventListener("hashchange", this.onHashChange);
    }
    if (this.injectedStyleEl) {
      this.injectedStyleEl.remove();
      this.injectedStyleEl = null;
    }
  },

  methods: {
    // Function ref: Vue calls it with the element when the Buy block mounts and
    // with null when it unmounts (it sits behind showStockAndLiveSection).
    observeMainBuy(el) {
      // Vue also calls function refs on every re-render; only act on a change.
      if (el === this.mainBuyEl) {
        return;
      }
      this.mainBuyEl = el;
      this.mainBuyObserver?.disconnect();
      this.mainBuyObserver = null;
      if (!el) {
        this.showPinnedHeader = false;
        return;
      }
      // The huge bottom margin counts "below the viewport" as intersecting, so
      // the only non-intersecting state is "scrolled up past the top". That
      // also catches jumps straight from below to above (anchors, restored
      // scroll), which a plain observer never reports.
      this.mainBuyObserver = new IntersectionObserver(
        ([entry]) => {
          this.showPinnedHeader = !entry.isIntersecting;
        },
        { rootMargin: "0px 0px 100000px 0px" },
      );
      this.mainBuyObserver.observe(el);
    },

    // selectOption (the choice-engine mutation) lives in the store; map it so
    // the template `@select-option="selectOption"` calls the store action.
    ...mapActions(useProductSelectorStore, ["selectOption"]),

    onFeaturedToggle(value) {
      this.isFeatured = value;
      this.saveFeatured();
    },

    saveFeatured() {
      this.isFeaturedSaving = true;

      this.adminProductsStore
        .toggleFeatured(this.product.id, this.isFeatured, this.featuredTheme)
        .then((response) => {
          this.isFeatured = response.data.product.isFeatured;
          this.featuredTheme = response.data.product.featuredTheme;
        })
        .catch((error) => {
          this.isFeatured = !this.isFeatured;
          alert("Error updating featured status: " + error?.response?.data?.message);
        })
        .finally(() => {
          this.isFeaturedSaving = false;
        });
    },

    cacheMockups() {
      this.isMockupCaching = true;

      this.adminProductsStore
        .cacheMockups(this.product.id)
        .then((response) => {
          alert(response.data.message);
        })
        .catch((error) => {
          alert("Error caching mockups: " + error?.response?.data?.message);
        })
        .finally(() => {
          this.isMockupCaching = false;
        });
    },

    updateSearchIndex() {
      this.isSearchIndexUpdating = true;

      this.adminProductsStore
        .updateSearchIndex(this.product.id)
        .then(() => {
          alert("Search index updated successfully.");
        })
        .catch((error) => {
          alert("Error updating search index: " + (error?.response?.data?.message || error.message));
        })
        .finally(() => {
          this.isSearchIndexUpdating = false;
        });
    },

    showChoice(choiceIndex) {
      const choiceExists = choiceIndex < this.activeChoices.length;

      if (choiceExists) {
        if (this.product.isExpired || this.isDisabledLegacyClockwiseOnDemandProduct) {
          return "nextChoice" in this.selectedOptions[choiceIndex];
        }

        return true;
      } else {
        return false;
      }
    },

    async addToBag() {
      let items = [
        {
          variant_id: this.selectedVariant.id,
          quantity: 1,
        },
      ];

      this.sendAnalyticData(true);

      let data = {
        items: JSON.stringify(items),
      };

      clearMessage(this);
      this.isSubmitting = true;
      try {
        // addItem hydrates the cart store, which the pane reads.
        await this.cartStore.addItem(data);
        this.toggleCheckout();
      } catch (errorResponse) {
        showError(this, retrieveErrors(errorResponse));
      } finally {
        this.isSubmitting = false;
      }
    },

    sendAnalyticData(isAddToBag) {
      const isCortex = Number(this.product.designer.id) === CORTEX_USER_ID;

      if (isAddToBag) {
        trackEvent("add_to_cart", {
          currency: "USD",
          value: this.selectedVariant.price,
          item_id: this.product.id,
          item_name: this.product.title,
          quantity: 1,
        });

        if (isCortex) {
          trackCortexPixel("AddToCart", {
            content_name: this.product.title,
            contents: [
              {
                id: this.product.id,
                quantity: 1,
              },
            ],
            content_type: "product",
            value: this.selectedVariant.price,
            currency: "USD",
          });
        }
      } else if (isCortex) {
        trackCortexPixel("PageView");
        trackCortexPixel("ViewContent", {
          content_name: this.product.title,
          content_ids: [this.product.id],
          content_type: "product",
        });
      }
    },

    viewSizeChart() {
      const productDetails = document.getElementsByClassName("nylon-ApparelResources");

      if (productDetails && productDetails.length) {
        Velocity(productDetails[0], "scroll");
      }
    },

    // `format` is a Luxon preset name ("DATETIME_FULL") or an Intl options object.
    toLocaleString(datetime, format) {
      return DateTime.fromISO(datetime).toLocaleString(typeof format === "string" ? DateTime[format] : format);
    },

    currentSubscription() {
      if (this.product.subscriptions.length) {
        return this.product.subscriptions.filter((s) => DateTime.now() < DateTime.fromISO(s.cloneAfter)).shift();
      }
    },

    createFromProductUrl() {
      const query = qs.stringify(
        {
          fromId: this.product.id,
          toType: "sticker",
        },
        true,
      );

      return `/create-a-product${query}`;
    },
  },
};
</script>
